import React, { useEffect, useState } from 'react';

// Working hours, holidays and the two automatic messages on chat channels (WhatsApp, Instagram, Messenger).
const DAYS = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [0, 'Sun']];
const fmtDate = (iso) => new Date(`${iso}T00:00:00+05:30`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

function AutoReplySettings({ api }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [newDay, setNewDay] = useState({ date: '', label: '' });

  const take = (d) => {
    setData(d);
    const c = d.config;
    setForm({ enabled: { ...c.enabled }, hours: { ...c.hours }, holidays: [...c.holidays], busyDelay: { mode: c.busyDelay.mode, minutes: c.busyDelay.minutes } });
  };
  useEffect(() => { api('/api/v1/support/auto-ack').then(take).catch((e) => setErr(e.message)); }, [api]);

  if (err) return <section className="cx-panel"><div className="cx-panel-b"><span className="cx-err">{err}</span></div></section>;
  if (!data || !form) return <section className="cx-panel"><div className="cx-skel" style={{ height: 240, margin: 16 }} /></section>;
  const edit = data.canEdit;
  const learned = data.config.busyDelay.learned;
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setMsg(null); };

  const save = async () => {
    setSaving(true); setMsg(null);
    try { take(await api('/api/v1/support/auto-ack', 'PUT', form)); setMsg({ ok: true, text: 'Saved. Changes apply within a minute.' }); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setSaving(false); }
  };
  const addHoliday = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDay.date)) { setMsg({ ok: false, text: 'Pick a date for the holiday.' }); return; }
    if (form.holidays.some((h) => h.date === newDay.date)) { setMsg({ ok: false, text: 'That date is already a holiday.' }); return; }
    set({ holidays: [...form.holidays, { date: newDay.date, label: newDay.label.trim() }].sort((a, b) => a.date.localeCompare(b.date)) });
    setNewDay({ date: '', label: '' });
  };
  const today = new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
  const upcoming = form.holidays.filter((h) => h.date >= today);
  const past = form.holidays.filter((h) => h.date < today);

  return (
    <>
      <section className="cx-panel" aria-labelledby="cx-hrs-s">
        <div className="cx-panel-h">
          <h2 id="cx-hrs-s">Working hours and auto-replies</h2>
          <span className={`cx-chip ${data.now.open ? 'cx-c-resolved' : 'cx-c-waiting'}`}>{data.now.open ? 'Team is in now' : `Closed · back ${data.now.nextOpenLabel}`}</span>
        </div>
        <div className="cx-panel-b cx-ar">
          <p className="cx-hint" style={{ margin: 0 }}>
            On WhatsApp, Instagram and Messenger, customers get one automatic message when the team is off or busy, with buttons: the after-hours reply offers Track my order (instant status and courier link for orders on their own WhatsApp number), Return / exchange and Browse fabrics; the busy note offers Track my order. It never counts as a reply: the chat stays new until someone answers.
            At most one per wait and per 12 hours, never for "ok / thanks", stickers or button taps. {edit ? '' : 'Only admins and Support managers can change these settings.'}
          </p>

          <fieldset className="cx-ar-row" disabled={!edit}>
            <legend>Working hours (India time)</legend>
            <div className="cx-ar-days">
              {DAYS.map(([d, l]) => (
                <label key={d} className="cx-ar-day">
                  <input type="checkbox" checked={form.hours.days.includes(d)} onChange={(e) => set({ hours: { ...form.hours, days: e.target.checked ? [...form.hours.days, d] : form.hours.days.filter((x) => x !== d) } })} /> {l}
                </label>
              ))}
            </div>
            <div className="cx-ar-times">
              <label htmlFor="cx-ar-start">From</label><input id="cx-ar-start" type="time" value={form.hours.start} onChange={(e) => set({ hours: { ...form.hours, start: e.target.value } })} />
              <label htmlFor="cx-ar-end">to</label><input id="cx-ar-end" type="time" value={form.hours.end} onChange={(e) => set({ hours: { ...form.hours, end: e.target.value } })} />
            </div>
          </fieldset>

          <fieldset className="cx-ar-row" disabled={!edit}>
            <legend>Holidays</legend>
            <p className="cx-hint" style={{ margin: '0 0 6px' }}>On these days the team is off all day: customers get the after-hours reply and it names the next working day.</p>
            {upcoming.length === 0 && <span className="cx-hint">No upcoming holidays.</span>}
            <ul className="cx-ar-hols">
              {upcoming.map((h) => (
                <li key={h.date}><b>{fmtDate(h.date)}</b><span>{h.label || 'Holiday'}</span>
                  {edit && <button className="cx-linkbtn" onClick={() => set({ holidays: form.holidays.filter((x) => x.date !== h.date) })} aria-label={`Remove ${h.label || h.date}`}>Remove</button>}
                </li>
              ))}
            </ul>
            {edit && (
              <div className="cx-ar-add">
                <input type="date" aria-label="Holiday date" min={today} value={newDay.date} onChange={(e) => setNewDay((n) => ({ ...n, date: e.target.value }))} />
                <input type="text" aria-label="Holiday name" placeholder="Name, e.g. Diwali" maxLength={60} value={newDay.label} onChange={(e) => setNewDay((n) => ({ ...n, label: e.target.value }))} />
                <button className="cx-btn cx-btn-sm" onClick={addHoliday}>Add holiday</button>
              </div>
            )}
            {past.length > 0 && <span className="cx-hint">{past.length} past holiday{past.length === 1 ? '' : 's'} kept for the record.</span>}
          </fieldset>

          <fieldset className="cx-ar-row" disabled={!edit}>
            <legend>Messages</legend>
            <label className="cx-ar-switch"><input type="checkbox" checked={form.enabled.after_hours} onChange={(e) => set({ enabled: { ...form.enabled, after_hours: e.target.checked } })} /> <span><b>After-hours reply</b>, sent straight away when a customer writes while the team is off</span></label>
            <label className="cx-ar-switch"><input type="checkbox" checked={form.enabled.busy} onChange={(e) => set({ enabled: { ...form.enabled, busy: e.target.checked } })} /> <span><b>Busy note</b>, sent during working hours when nobody has answered for {data.busyMinutesNow} minutes</span></label>
            <div className="cx-ar-delay">
              <label><input type="radio" name="cx-ar-mode" checked={form.busyDelay.mode === 'auto'} onChange={() => set({ busyDelay: { ...form.busyDelay, mode: 'auto' } })} /> Learn from our reply times (recommended)</label>
              <label><input type="radio" name="cx-ar-mode" checked={form.busyDelay.mode === 'fixed'} onChange={() => set({ busyDelay: { ...form.busyDelay, mode: 'fixed' } })} /> Fixed:</label>
              <input type="number" min={5} max={120} aria-label="Minutes before the busy note" value={form.busyDelay.minutes} disabled={!edit || form.busyDelay.mode !== 'fixed'} onChange={(e) => set({ busyDelay: { ...form.busyDelay, minutes: e.target.value } })} /> <span>minutes</span>
            </div>
            <p className="cx-hint" style={{ margin: 0 }}>
              {learned ? (learned.enoughData
                ? `Last 14 days: the team's typical first reply took ${learned.p50} min over ${learned.n} chats, so the note waits ${learned.minutes} min (kept between 15 and 30). Updated every morning.`
                : `Only ${learned.n} chats in the last 14 days, so the note waits 20 min until there are 30 to learn from.`)
                : 'The waiting time is learned every morning from the last 14 days.'}
            </p>
          </fieldset>

          <p className="cx-hint" style={{ margin: 0 }}>The wording of these messages and their buttons is edited in <b>WhatsApp → Chat flows</b>, with a preview and a test on your own WhatsApp.</p>

          {msg && <div className={msg.ok ? 'cx-hint' : 'cx-err cx-err-box'} role={msg.ok ? 'status' : 'alert'} style={{ margin: 0 }}>{msg.text}</div>}
          {edit && <div><button className="cx-btn cx-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button></div>}
        </div>
        <div className="cx-panel-note">
          Last 14 days: {data.last14.after_hours || 0} after-hours repl{data.last14.after_hours === 1 ? 'y' : 'ies'}, {data.last14.busy || 0} busy note{data.last14.busy === 1 ? '' : 's'} sent
          {Object.keys(data.last14.byChannel || {}).length ? ` (${Object.entries(data.last14.byChannel).map(([c, n]) => `${c} ${n}`).join(', ')})` : ''}.
          {' '}Button taps: Track my order {data.last14.taps?.AA_TRACK || 0}, Return / exchange {data.last14.taps?.AA_RETURN || 0}, Browse fabrics {data.last14.taps?.AA_BROWSE || 0}.
        </div>
      </section>
    </>
  );
}

export default AutoReplySettings;
